import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { deletePayment, getpaymentDetails } from "../../../../redux/slices/paymentSlice";
import { FiArrowLeft, FiDollarSign, FiCreditCard, FiCheckCircle, FiXCircle, FiCalendar, FiUser, FiHash, FiTrash2 } from "react-icons/fi";

const PaymentDetail = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const [deleteModal, setDeleteModal] = useState(false);
  const { paymentDetails } = useSelector((state) => state.paymentSlice);
  const [isLoading, setIsLoading] = useState(false);

  const location = useLocation();

  useEffect(() => {
    setIsLoading(true);
    dispatch(getpaymentDetails(id, setIsLoading));
  }, [id, dispatch]);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleString();
    } catch (error) {
      return dateString;
    }
  };

  const handleDelete = () => {
    dispatch(deletePayment(id, setIsLoading, setDeleteModal));
    navigate(-1);
  };

  return (
    <div className="container-fluid">
      <div className="row">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h4 className="mb-0">Payment Details</h4>
            <div>
              <button
                className="btn btn-outline-danger btn-icon-text mr-2"
                onClick={handleDelete}
              >
                <FiTrash2 className="mr-1" /> Delete
              </button>
              <button
                onClick={() => navigate("/payments", {
                  state: location.state, // 👈 preserves pagination
                })}
                className="btn btn-outline-secondary btn-icon-text"
              >
                <FiArrowLeft className="mr-1" /> Back
              </button>
            </div>
          </div>

          {isLoading ? (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '300px' }}>
              <div className="spinner-border text-primary" role="status">
                <span className="sr-only"></span>
              </div>
            </div>
          ) : (
            <div className="row">
              <div className="col-12">
                <div className="card">
                  <div className="card-body">
                    <div className="row">
                      <div className="col-md-6">
                        <div className="form-group mb-3">
                          <label className="text-muted">Payment ID</label>
                          <div className="form-control bg-light">
                            <div className="d-flex align-items-center">
                              <FiHash className="mr-2 text-primary" />
                              {paymentDetails?._id || 'N/A'}
                            </div>
                          </div>
                        </div>

                        <div className="form-group mb-3">
                          <label className="text-muted">
                            {paymentDetails?.orderCode
                              ? 'Order ID'
                              : paymentDetails?.discountBookingId
                                ? 'Discount Booking ID'
                                : paymentDetails?.giftCertificateId
                                  ? 'Gift Certificate ID'
                                  : 'Reference ID'}
                          </label>
                          <div className="form-control bg-light">
                            <div className="d-flex align-items-center">
                              <FiCreditCard className="mr-2 text-primary" />
                              {paymentDetails?.orderCode || paymentDetails?.discountBookingId || paymentDetails?.giftCertificateId ? (
                                <a
                                  href={`#${paymentDetails?.orderCode
                                    ? `/bookings/program/${paymentDetails.orderCode}`
                                    : paymentDetails?.discountBookingId
                                      ? `/bookings/discount/${paymentDetails.discountBookingId}`
                                      : `/gift/${paymentDetails.giftCertificateId}/details`
                                    }`}
                                  onClick={(e) => {
                                    e.preventDefault();
                                    if (paymentDetails?.orderCode) {
                                      navigate(`/bookings/program/${paymentDetails.orderCode}`);
                                    } else if (paymentDetails?.discountBookingId) {
                                      navigate(`/bookings/discount/${paymentDetails.discountBookingId}`);
                                    } else if (paymentDetails?.giftCertificateId) {
                                      navigate(`/gift/${paymentDetails.giftCertificateId}/details`);
                                    }
                                  }}
                                  className="text-primary"
                                >
                                  {paymentDetails?.orderCode || paymentDetails?.discountBookingId || paymentDetails?.giftCertificateId}
                                </a>
                              ) : 'N/A'}
                            </div>
                          </div>
                        </div>

                        <div className="form-group mb-3">
                          <label className="text-muted">Customer Name</label>
                          <div className="form-control bg-light">
                            <div className="d-flex align-items-center">
                              <FiUser className="mr-2 text-primary" />
                              {paymentDetails?.createdBy?._id ? (
                                <a
                                  href={`#/user/${paymentDetails.createdBy._id}`}
                                  onClick={(e) => {
                                    e.preventDefault();
                                    navigate(`/user/${paymentDetails.createdBy._id}`);
                                  }}
                                  className="text-primary"
                                >
                                  {paymentDetails.createdBy.fullName || 'View User'}
                                </a>
                              ) : (
                                paymentDetails?.createdBy?.fullName || 'N/A'
                              )}
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="col-md-6">
                        <div className="form-group mb-3">
                          <label className="text-muted">Amount</label>
                          <div className="form-control bg-light">
                            <div className="d-flex align-items-center">
                              <FiDollarSign className="mr-2 text-primary" />
                              {paymentDetails?.amount ? `${paymentDetails.amount} ${paymentDetails.currency || ''}`.trim() : '0'}
                            </div>
                          </div>
                        </div>

                        <div className="form-group mb-3">
                          <label className="text-muted">Status</label>
                          <div className={`form-control bg-light ${paymentDetails?.paymentStatus === 'success' ? 'text-success' : 'text-danger'}`}>
                            <div className="d-flex align-items-center">
                              {paymentDetails?.paymentStatus === 'success' ? (
                                <FiCheckCircle className="mr-2" />
                              ) : (
                                <FiXCircle className="mr-2" />
                              )}
                              {paymentDetails?.paymentStatus === 'success' ? 'Success' : 'Failed'}
                            </div>
                          </div>
                        </div>

                        <div className="form-group mb-3">
                          <label className="text-muted">Transaction Date</label>
                          <div className="form-control bg-light">
                            <div className="d-flex align-items-center">
                              <FiCalendar className="mr-2 text-primary" />
                              {formatDate(paymentDetails?.createdAt)}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {paymentDetails?.transactionId && (
                      <div className="form-group mt-3">
                        <label className="text-muted">Transaction ID</label>
                        <div className="form-control bg-light">
                          <div className="d-flex align-items-center">
                            <FiCreditCard className="mr-2 text-primary" />
                            {paymentDetails.transactionId}
                          </div>
                        </div>
                      </div>
                    )}

                    {paymentDetails?.other && Object.keys(paymentDetails.other).length > 0 && (
                      <div className="mt-4">
                        <h5 className="mb-3">Additional Information</h5>
                        <div className="row">
                          {Object.entries(paymentDetails.other).map(([key, value]) => (
                            <div key={key} className="col-md-6 mb-3">
                              <div className="form-group">
                                <label className="text-muted">
                                  {key
                                    .replace(/([A-Z])/g, ' $1')
                                    .replace(/^./, (str) => str.toUpperCase())}
                                </label>
                                <div className="form-control bg-light">
                                  {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

          )}
        </div>
      </div>
    </div>
  );
};

export default PaymentDetail;
